


import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Grid, TextField, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Paper, IconButton, Alert, Autocomplete, Divider, CircularProgress,
  Chip, InputAdornment, Tooltip
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  LocalShipping as ShippingIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API;

const CreatePurchaseOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dropdowns data
  const [distributors, setDistributors] = useState([]);
  const [allMedicines, setAllMedicines] = useState([]); // All medicines
  const [filteredMedicines, setFilteredMedicines] = useState([]); // Medicines for selected distributor
  
  // Form data
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [notes, setNotes] = useState('');

  // Items
  const [items, setItems] = useState([]);
  
  // Validation warnings for pre-filled items
  const [prefilledWarnings, setPrefilledWarnings] = useState([]);
  
  // Search for adding items
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [quantity, setQuantity] = useState('');
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setDataLoading(true);
    
    // Set default delivery date (7 days from now)
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 7);
    setExpectedDeliveryDate(defaultDate.toISOString().split('T')[0]);
    
    // Load distributors and medicines first
    await Promise.all([loadDistributors(), loadMedicines()]);
    
    setDataLoading(false);
  };

  // Handle prefilled data after distributors are loaded
  useEffect(() => {
    if (!dataLoading && distributors.length > 0 && location.state?.prefilledData) {
      const prefilled = location.state.prefilledData;
      console.log(distributors)
      // Set distributor if provided
      if (prefilled.distributor_id) {
        const dist = distributors.find(d => d.company_name === prefilled.distributor_id);
        if (dist) {
          setSelectedDistributor(dist);
          console.log(dist)
          // Set payment terms from distributor
          if (dist.payment_terms) {
            setPaymentTerms(dist.payment_terms);
          }
          
          //  VALIDATE PREFILLED ITEMS against distributor
          if (prefilled.items && prefilled.items.length > 0) {
            validatePrefilledItems(prefilled.items, dist);
          }
        } else {
          console.warn('Distributor not found:', prefilled.distributor_id);
        }
      }
      
      // Set items if provided
      if (prefilled.items && prefilled.items.length > 0) {
        console.log(' Setting items:', prefilled.items.length);
        setItems(prefilled.items);
      }
      
      // Set notes if provided
      if (prefilled.notes) {
        setNotes(prefilled.notes);
      }
    }
  }, [dataLoading, distributors, location.state]);

  //  NEW: Filter medicines when distributor changes
  useEffect(() => {
    if (selectedDistributor) {
      filterMedicinesByDistributor(selectedDistributor);
    } else {
      setFilteredMedicines(allMedicines);
      console.log(allMedicines)
    }
  }, [selectedDistributor, allMedicines]);

  //  NEW: Validate prefilled items against distributor
  const validatePrefilledItems = (prefilledItems, distributor) => {
    const warnings = [];
    const distributorMedicineIds = distributor.distributor_medicine_ids || [];
    console.log(distributor.distributor_medicine_ids)
    const distributorIds = distributorMedicineIds.map(
        m => m.medicine_id
      );

    prefilledItems.forEach(item => {
      // Check if medicine is supplied by this distributor
      const isSupplied = distributorIds.includes(item.medicine_id);
      
      if (!isSupplied) {
        warnings.push({
          medicine_id: item.medicine_id,
          medicine_name: item.medicine_name,
          manufacturer: item.manufacturer
        });
      }
    });
    
    if (warnings.length > 0) {
      console.warn(' Some pre-filled medicines are not supplied by selected distributor:', warnings);
      setPrefilledWarnings(warnings);
    }
  };

  // 👇 NEW: Filter medicines based on selected distributor
  const filterMedicinesByDistributor = (distributor) => {
    if (!distributor) {
      setFilteredMedicines(allMedicines);
      return;
    }

    // Get distributor's medicine IDs
    const distributorMedicineIds =
      distributor.distributor_medicine_ids?.map(m => m.medicine_id) || [];
    

    if (distributorMedicineIds.length === 0) {
      console.warn(' Distributor has no associated medicines',distributorMedicineIds);
      setFilteredMedicines(allMedicines); // Show all if no filter available
      return;
    }

    // Filter medicines that belong to this distributor
    const filtered = allMedicines.filter(medicine => 
      distributorMedicineIds.includes(medicine.medicine_id)
    );
    console.log(distributorMedicineIds)
    console.log(` Filtered medicines for ${distributor.company_name}: ${filtered.length} out of ${allMedicines}`);
    setFilteredMedicines(filtered);
  };

  const loadDistributors = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/distributors`);
      const distributorData = response.data.distributors || response.data || [];
      setDistributors(Array.isArray(distributorData) ? distributorData : []);
      console.log(' Distributors loaded:', distributorData.length);
    } catch (error) {
      console.error('Error loading distributors:', error);
      setDistributors([]);
      setError('Failed to load distributors. Please refresh the page.');
    }
  };

  const loadMedicines = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/medicine/medicines`);
      const medicineData = response.data.medicines || response.data || [];
      
      // Process medicines and filter out 0 stock items
      const processedMedicines = medicineData
        .map(med => ({
          ...med,
          id: med.medicine_id
        }))

      setAllMedicines(Array.isArray(processedMedicines) ? processedMedicines : []);
      setFilteredMedicines(Array.isArray(processedMedicines) ? processedMedicines : []);
    } catch (error) {
      console.error('Error loading medicines:', error);
      setAllMedicines([]);
      setFilteredMedicines([]);
      setError('Failed to load medicines. Please refresh the page.');
    }
  };

  const handleDistributorChange = (newDistributor) => {
    setSelectedDistributor(newDistributor);
    console.log(newDistributor)
    setSelectedMedicine(null);
    // Update payment terms
    if (newDistributor?.payment_terms) {
      setPaymentTerms(newDistributor.payment_terms);
    }
    //RE-VALIDATE existing items against new distributor
    if (items.length > 0 && newDistributor) {
      validatePrefilledItems(items, newDistributor);
    } else {
      setPrefilledWarnings([]);
    }
  };

  const handleAddItem = () => {
    if (!selectedMedicine) {
      setError('Please select a medicine');
      return;
    }
    
    if (!quantity || quantity <= 0) {
      setError('Please enter a valid quantity');
      return;
    }

    const medicineId = selectedMedicine.medicine_id || selectedMedicine.id;
    
    if (!medicineId) {
      console.error('❌ CRITICAL ERROR: No medicine_id found!', selectedMedicine);
      setError('Medicine ID is missing. Please contact support.');
      return;
    }

    // Check if medicine already exists
    const existingIndex = items.findIndex(item => item.medicine_id === medicineId);
    if (existingIndex !== -1) {
      setError('Medicine already added. Please update the existing item.');
      return;
    }

    const newItem = {
      medicine_id: medicineId,
      medicine_name: selectedMedicine.name || '',
      generic_name: selectedMedicine.generic_name || selectedMedicine.examiner_salt || '',
      manufacturer: selectedMedicine.manufacturer || '',
      quantity_ordered: parseInt(quantity),
      quantity_received: 0,
      quantity_pending: parseInt(quantity),
      unit_price: 0,
      purchase_price_estimate: 0,
      discount_percentage: 0,
      gst_percentage: 12,
      total: 0,
      batch_number: '',
      expiry_date: '',
      manufacturing_date: '',
      mrp: 0,
      selling_price: 0,
      is_received: false
    };

    setItems([...items, newItem]);
    
    // Reset form
    setSelectedMedicine(null);
    setQuantity('');
    setError('');
  };

  const handleRemoveItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
    //  RE-VALIDATE after removing item
    if (selectedDistributor && updatedItems.length > 0) {
      validatePrefilledItems(updatedItems, selectedDistributor);
    } else {
      setPrefilledWarnings([]);
    }
  };

  const handleUpdateItem = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;
    
    if (field === 'quantity_ordered') {
      updatedItems[index].quantity_pending = value;
    }
    
    setItems(updatedItems);
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    
    // Validation
    if (!selectedDistributor) {
      setError('Please select a distributor');
      return;
    }
    
    if (items.length === 0) {
      setError('Please add at least one item');
      return;
    }
    
    //WARN if there are items not supplied by distributor
    if (prefilledWarnings.length > 0) {
      setError(`Warning: ${prefilledWarnings.length} item(s) are not supplied by the selected distributor. Please review and remove them.`);
      return;
    }
    // Verify all items have medicine_id
    const invalidItems = items.filter(item => !item.medicine_id);
    if (invalidItems.length > 0) {
      console.error(' Items missing medicine_id:', invalidItems);
      setError('Some items are missing medicine IDs. Please refresh and try again.');
      return;
    }
    console.log('✅ Submitting PO with items:', items.map(i => ({ 
      medicine_id: i.medicine_id, 
      name: i.medicine_name 
    })));
    
    const poData = {
      distributor_id: selectedDistributor.distributor_id,
      expected_delivery_date: expectedDeliveryDate,
      items: items,
      subtotal: 0,
      total_discount: 0,
      total_gst: 0,
      shipping_charges: 0,
      grand_total: 0,
      payment_terms: paymentTerms,
      notes: notes,
      created_by: localStorage.getItem('user_id') || '',
      approval_status:"approved"
    };

    setLoading(true);
    try {
      console.log(poData)
      const response = await axios.post(
        `${API_BASE_URL}/purchase-orders`,
        poData
      );
      
      setSuccess('Purchase order created successfully!');
      setTimeout(() => {
        navigate(`/purchase-orders/${response.data.purchase_order.purchase_order.id}`);
      }, 2000);
      
    } catch (error) {
      console.error('Error creating PO:', error);
      const errorMsg = error.response?.data?.error || 'Failed to create purchase order';
      setError(errorMsg);
      
      if (error.response?.data) {
        console.error('Server error details:', error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const DRAWER_WIDTH = '300px';

  if (dataLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        ml: { xs: 0, md: DRAWER_WIDTH }
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={50} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading purchase order form...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f8f9fa', 
      ml: { xs: 0, md: DRAWER_WIDTH }, 
      p: { xs: 2, md: 3 } 
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={() => navigate('/purchase-orders')}
            sx={{ 
              mr: 2,
              bgcolor: 'white',
              boxShadow: 1,
              '&:hover': { bgcolor: '#f5f5f5' }
            }}
          >
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Create Purchase Order
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {location.state?.prefilledData ? 
                `Creating PO from stock suggestions (${items.length} items)` :
                'Add medicines and distributor details to create a new PO'
              }
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={loading || items.length === 0}
          sx={{ 
            bgcolor:"#006D77",
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            boxShadow: 2
          }}
        >
          {loading ? 'Creating...' : 'Create Purchase Order'}
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }} 
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      {/*  NEW: Warning for medicines not supplied by distributor */}
      {prefilledWarnings.length > 0 && (
        <Alert 
          severity="error" 
          icon={<WarningIcon />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {prefilledWarnings.length} item(s) are NOT supplied by {selectedDistributor?.company_name}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mb: 1 }}>
            The following medicines should be removed or you should select a different distributor:
          </Typography>
          <Box sx={{ mt: 1 }}>
            {prefilledWarnings.map((warning, idx) => (
              <Chip
                key={idx}
                label={warning.medicine_name}
                size="small"
                color="error"
                variant="outlined"
                sx={{ mr: 1, mb: 1 }}
                onDelete={() => {
                  // Remove this item from the list
                  const updatedItems = items.filter(item => item.medicine_id !== warning.medicine_id);
                  setItems(updatedItems);
                  
                  // Re-validate
                  if (selectedDistributor && updatedItems.length > 0) {
                    validatePrefilledItems(updatedItems, selectedDistributor);
                  } else {
                    setPrefilledWarnings([]);
                  }
                }}
              />
            ))}
          </Box>
        </Alert>
      )}

      {/* Show info if prefilled */}
      {location.state?.prefilledData && prefilledWarnings.length === 0 && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            Pre-filled from Stock Suggestions
          </Typography>
          <Typography variant="caption">
            {items.length} items have been added. You can modify quantities or add more items before submitting.
          </Typography>
        </Alert>
      )}

      {/* PO Details Card */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={600}>
              Purchase Order Details
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                value={selectedDistributor}
                onChange={(event, newValue) => handleDistributorChange(newValue)}
                options={distributors}
                getOptionLabel={(option) => option.company_name || option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Select Distributor" 
                    sx={{
                      width: {
                        xs: '100%',
                        sm: 400,
                        md: 500,
                      },
                    }}
                    required
                    placeholder="Search and select distributor..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const getAddressString = (addr) => {
                    if (!addr) return '';
                    if (typeof addr === 'string') return addr;
                    
                    const { street = '', city = '', state = '', pincode = '', country = '' } = addr;
                    return [street, city, state, pincode, country]
                      .filter(Boolean)
                      .join(', ');
                  };

                  return (
                    <li {...props} key={option.id}>
                      <Box sx={{ py: 1.5, width: '100%' }}>
                        <Typography variant="body1" fontWeight={600}>
                          {option.company_name}
                        </Typography>
                        
                        <Typography variant="caption" color="text.secondary" display="block">
                          👤 {option.name}
                          {" "}📞 {option.phone}
                          {option.email && ` • 📧 ${option.email}`}
                        </Typography>
                        
                        <Typography variant="caption" color="text.secondary" display="block">
                          GST: {option.gst_number || 'N/A'}
                        </Typography>
                        
                        {option.address && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            📍 {getAddressString(option.address)}
                          </Typography>
                        )}
                        
                        {/*  NEW: Show medicine count */}
                        {option.distributor_medicine_ids && option.distributor_medicine_ids.length > 0 && (
                          <Chip
                            label={`${option.distributor_medicine_ids.length} medicines`}
                            size="small"
                            sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </li>
                  );
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Expected Delivery Date"
                value={expectedDeliveryDate}
                inputProps={{
                  min: today,  
                }}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  if (selectedDate >= today) {
                    setExpectedDeliveryDate(selectedDate);
                  }
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ShippingIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Payment Terms"
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                placeholder="e.g., Net 30 days, Advance payment"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional instructions or remarks..."
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Add Items Card */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Add Medicine Items
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {selectedDistributor 
              ? `Showing ${filteredMedicines.length} medicines supplied by ${selectedDistributor.company_name}`
              : 'Select a distributor to filter medicines'}
          </Typography>
          
          {/*  NEW: Warning if no distributor selected */}
          {!selectedDistributor && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Please select a distributor first to see available medicines
            </Alert>
          )}
          
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={8}>
              <Autocomplete
                freeSolo
                value={selectedMedicine}
                sx={{
                  width: {
                    xs: '100%',
                    sm: 400,
                    md: 500,
                  },
                }}
                disabled={!selectedDistributor} //  Disable if no distributor
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                    setSelectedMedicine({
                      id: `custom-${newValue}`,
                      medicine_id: `custom-${newValue}`,
                      name: newValue,
                      generic_name: '',
                      manufacturer: '',
                      isCustom: true,
                    });
                  } else if (newValue?.inputValue) {
                    setSelectedMedicine({
                      id: `custom-${newValue.inputValue}`,
                      medicine_id: `custom-${newValue.inputValue}`,
                      name: newValue.inputValue,
                      generic_name: '',
                      manufacturer: '',
                      isCustom: true,
                    });
                  } else {
                    setSelectedMedicine(newValue);
                  }
                }}
                options={filteredMedicines} //  Use filtered medicines
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.name || '';
                }}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                filterOptions={(options, params) => {
                  const filtered = options.filter(option =>
                    option.name?.toLowerCase().includes(params.inputValue.toLowerCase()) ||
                    option.generic_name?.toLowerCase().includes(params.inputValue.toLowerCase()) ||
                    option.manufacturer?.toLowerCase().includes(params.inputValue.toLowerCase())
                  );

                  if (
                    params.inputValue !== '' &&
                    !options.some(opt =>
                      opt.name?.toLowerCase() === params.inputValue.toLowerCase()
                    )
                  ) {
                    filtered.push({
                      inputValue: params.inputValue,
                      name: `Add "${params.inputValue}"`,
                    });
                  }

                  return filtered;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={selectedDistributor ? "Search Medicine" : "Select distributor first"}
                    placeholder={selectedDistributor ? "Select or type medicine name" : ""}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id || option.name}>
                    <Box sx={{ py: 1 }}>
                      <Typography fontWeight={500}>
                        {option.name}
                      </Typography>
                      {!option.inputValue && (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            {option.generic_name}
                          </Typography>
                          {option.manufacturer && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              🏭 {option.manufacturer}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Quantity"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                inputProps={{ min: 1 }}
                placeholder="Enter qty"
                disabled={!selectedDistributor}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                disabled={!selectedDistributor}
                sx={{ 
                  bgcolor:"#006D77",
                  height: 56,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2
                }}
              >
                Add Item
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Items Table Card */}
      <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Order Items
            </Typography>
            <Chip 
              label={`${items.length} ${items.length === 1 ? 'Item' : 'Items'}`} 
              color="primary"
              sx={{ fontWeight: 600 }}
            />
          </Box>
          
          {items.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No items added yet. Use the form above to add medicines to your purchase order.
            </Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Medicine Details</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => {
                      //  Check if this item is in the warnings list
                      const isNotSupplied = prefilledWarnings.some(w => w.medicine_id === item.medicine_id);
                      
                      return (
                        <TableRow 
                          key={index} 
                          hover
                          sx={{
                            bgcolor: isNotSupplied ? '#fef2f2' : 'inherit'
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {isNotSupplied && (
                                <Tooltip title="Not supplied by selected distributor">
                                  <WarningIcon color="error" fontSize="small" />
                                </Tooltip>
                              )}
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {item.medicine_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.generic_name}
                                </Typography>
                                {item.manufacturer && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    🏭 {item.manufacturer}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity_ordered}
                              onChange={(e) => handleUpdateItem(index, 'quantity_ordered', parseInt(e.target.value) || 0)}
                              inputProps={{ 
                                min: 1, 
                                style: { textAlign: 'center' } 
                              }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {isNotSupplied ? (
                              <Chip
                                label="Not Supplied"
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            ) : (
                              <Chip
                                label="Valid"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Remove item">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveItem(index)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Action Buttons */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/purchase-orders')}
                  sx={{ 
                    px: 4,
                    borderColor:"#006D77",
                    textTransform: 'none',
                    borderRadius: 2,
                    color:"#006D77"
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  disabled={loading || prefilledWarnings.length > 0}
                  sx={{ 
                    bgcolor:"#006D77",
                    px: 4,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 2
                  }}
                >
                  {loading ? 'Creating...' : 'Create Purchase Order'}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreatePurchaseOrder;